/**
 * Standing blocks — the rows that used to live in the Work Blocks sheet, one
 * per kind of block rather than one per copy ("PMP, 3 a week"). Order is
 * priority: earlier blocks get first pick of the week's open time.
 *
 * Folded away by default on the Week view: you set these up once and change
 * them rarely, but each one's last result stays one click away.
 */

import { useMemo, useState } from "react";

import { card, Dot } from "@/components/atoms";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast } from "@/context/ToastContext";
import { useWorkBlockMutations } from "@/hooks/useWorkBlocks";
import { toIsoLocal } from "@/lib/timeRollup";
import type { Project } from "@/lib/types";
import {
  blockTitle, DAY_KEYS, DAY_LABEL, formatTime, hmm, localStamp, parseTime, perWeekOf, purposeOf,
  type BlockPurpose, type BlockSettings, type DayKey, type WorkBlock,
} from "@/lib/workBlocks";

import { arrowBtn, ghost, input, label, Modal, primary, Seg } from "./parts";

const LENGTHS = [15, 30, 45, 60, 90, 120, 180];

/** Minutes as h:mm, the one format for time across the Planner: 90 → "1:30", 0 → "0:00". */
export const durationText = (min: number): string => hmm(min) || "0:00";

export function StandingBlocks({ blocks, projects, settings, open, onToggle }: {
  blocks: WorkBlock[];
  projects: Project[];
  settings: BlockSettings;
  open: boolean;
  onToggle: () => void;
}) {
  const toast = useToast();
  const { saveBlock, deleteBlock, reorder, saveSettings } = useWorkBlockMutations();
  const [editing, setEditing] = useState<WorkBlock | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  const standing = useMemo(
    () => blocks.filter((b) => !b.week).sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [blocks],
  );
  const projectById = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);
  const names = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p.name])), [projects]);

  const totals = standing.reduce((t, b) => {
    if (b.enabled === false) return t;
    const min = Number(b.durationMin || 0) * perWeekOf(b);
    t[purposeOf(b)] += min;
    return t;
  }, { project: 0, routine: 0 } as Record<BlockPurpose, number>);

  function move(index: number, dir: -1 | 1) {
    const j = index + dir;
    if (j < 0 || j >= standing.length) return;
    const next = [...standing];
    [next[index], next[j]] = [next[j], next[index]];
    reorder.mutate(next, { onError: () => toast("Could not save the new order") });
  }

  async function toggleEnabled(b: WorkBlock) {
    try { await saveBlock.mutateAsync({ ...b, enabled: b.enabled === false }); }
    catch { toast("Could not save that block"); }
  }

  return (
    <div style={{ ...card, padding: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", flexWrap: "wrap" }}>
        <button onClick={onToggle} aria-expanded={open} style={{
          border: "none", background: "none", padding: 0, cursor: "pointer", color: "var(--ink)",
          display: "flex", alignItems: "center", gap: 8, fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)",
        }}>
          <span style={{ display: "inline-block", width: 12, color: "var(--muted)", transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }}>›</span>
          Standing blocks
        </button>
        <span style={{ fontSize: 12.5, color: "var(--muted)" }}>
          {standing.length === 0
            ? "None yet"
            : `${durationText(totals.project + totals.routine)} a week · ${durationText(totals.project)} project work · ${durationText(totals.routine)} routine`}
        </span>
        {open && (
          <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button onClick={() => setShowSettings(true)} style={ghost}>Hours & defaults</button>
            <button onClick={() => setEditing(blankBlock(standing.length))} style={primary}>+ Add block</button>
          </span>
        )}
      </div>

      {open && (
        <div style={{ borderTop: "1px solid var(--line)" }}>
          {standing.length === 0 && (
            <div style={{ padding: "22px 16px", fontSize: 13, color: "var(--muted)" }}>
              Blocks you want every week — a project's regular time, or routine work that isn't on a task list.
              The Sunday run places them around your meetings.
            </div>
          )}
          {standing.map((b, i) => {
            const project = b.project ? projectById.get(b.project) : undefined;
            const off = b.enabled === false;
            return (
              <div key={b.id} className="row-hover" onClick={() => setEditing(b)} style={{
                display: "grid", gridTemplateColumns: "24px minmax(0, 2fr) minmax(0, 1.6fr) minmax(0, 2fr) auto",
                gap: 12, alignItems: "center", padding: "10px 16px", borderBottom: "1px solid var(--tint2)",
                cursor: "pointer", opacity: off ? 0.55 : 1,
              }}>
                <span style={{ display: "flex", flexDirection: "column", gap: 2 }} onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" style={arrowBtn(i === 0)}>▲</button>
                  <button onClick={() => move(i, 1)} disabled={i === standing.length - 1} aria-label="Move down" style={arrowBtn(i === standing.length - 1)}>▼</button>
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13.5, fontWeight: 500, color: "var(--ink)" }}>
                    {project && <Dot color={project.color} />}
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{blockTitle(b, settings, names)}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 3 }}>
                    <PurposePill purpose={purposeOf(b)} />
                    {" "}{Number(b.durationMin)} min × {perWeekOf(b)} a week
                  </div>
                </div>
                <div style={{ fontSize: 12, color: "var(--ink3)", minWidth: 0 }}>{whenText(b, settings)}</div>
                <div style={{ fontSize: 12, color: b.lastStatus?.includes("no slot") || b.lastStatus?.startsWith("No open") ? "var(--warn)" : "var(--muted)", minWidth: 0 }}
                  title={b.lastRunAt ? `Last run ${new Date(b.lastRunAt).toLocaleString()}` : undefined}>
                  {b.lastStatus || "Not run yet"}
                </div>
                <span onClick={(e) => e.stopPropagation()}>
                  <Switch on={!off} onChange={() => void toggleEnabled(b)} label={off ? "Turn on" : "Pause"} />
                </span>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <BlockEditor
          block={editing}
          projects={projects}
          settings={settings}
          saving={saveBlock.isPending}
          onCancel={() => setEditing(null)}
          onSave={async (next) => {
            try { await saveBlock.mutateAsync(next); toast(next.id ? "Block saved" : "Block added"); setEditing(null); }
            catch { toast("Could not save that block"); }
          }}
          onDelete={editing.id ? async () => {
            try { await deleteBlock.mutateAsync(editing.id); toast("Block deleted"); setEditing(null); }
            catch { toast("Could not delete that block"); }
          } : undefined}
        />
      )}

      {showSettings && (
        <SettingsDialog
          settings={settings}
          saving={saveSettings.isPending}
          onCancel={() => setShowSettings(false)}
          onSave={async (next) => {
            try { await saveSettings.mutateAsync(next); toast("Saved"); setShowSettings(false); }
            catch { toast("Could not save those settings"); }
          }}
        />
      )}
    </div>
  );
}

export function PurposePill({ purpose }: { purpose: BlockPurpose }) {
  const routine = purpose === "routine";
  return (
    <span style={{
      padding: "1px 7px", borderRadius: 999, fontSize: 11,
      background: routine ? "var(--tint)" : "var(--accentTint)", color: routine ? "var(--ink3)" : "var(--accent)",
    }}>{routine ? "Routine" : "Project work"}</span>
  );
}

/** "Tue, ~9:00 AM · 8:00 AM–2:00 PM · Mon–Fri". Only what differs from the defaults. */
function whenText(b: WorkBlock, s: BlockSettings): string {
  const parts: string[] = [];
  if (b.preferredDay) parts.push(`Prefers ${DAY_LABEL[b.preferredDay]}`);
  const pref = parseTime(b.preferredTime);
  if (pref != null) parts.push(`~${formatTime(pref)}`);
  const early = parseTime(b.earliest);
  const late = parseTime(b.latest);
  if (early != null || late != null) {
    parts.push(`${formatTime(early ?? parseTime(s.workStart) ?? 480)}–${formatTime(late ?? parseTime(s.workEnd) ?? 960)}`);
  }
  if (b.days?.length) parts.push(daysText(b.days));
  return parts.join(" · ") || "Any time in the work day";
}

export function daysText(days: DayKey[]): string {
  const idx = DAY_KEYS.map((d) => days.includes(d));
  const on = DAY_KEYS.filter((_, i) => idx[i]);
  const first = idx.indexOf(true);
  const last = idx.lastIndexOf(true);
  const contiguous = on.length > 2 && idx.slice(first, last + 1).every(Boolean);
  return contiguous ? `${DAY_LABEL[on[0]]}–${DAY_LABEL[on[on.length - 1]]}` : on.map((d) => DAY_LABEL[d]).join(", ");
}

function blankBlock(position: number): WorkBlock {
  return { id: "", durationMin: 60, perWeek: 1, purpose: "project", enabled: true, position, days: [] };
}

/* -- Editors ---------------------------------------------------------------- */

function BlockEditor({ block, projects, settings, saving, onSave, onCancel, onDelete }: {
  block: WorkBlock; projects: Project[]; settings: BlockSettings; saving: boolean;
  onSave: (b: WorkBlock) => void; onCancel: () => void; onDelete?: () => void;
}) {
  const [draft, setDraft] = useState<WorkBlock>(block);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const set = (patch: Partial<WorkBlock>) => { setDraft((d) => ({ ...d, ...patch })); setError(null); };
  const live = projects.filter((p) => p.status !== "archived" && p.kind !== "inbox").sort((a, b) => a.name.localeCompare(b.name));
  const names = Object.fromEntries(projects.map((p) => [p.id, p.name]));
  const days = draft.days ?? [];

  function submit() {
    if (!draft.project && !draft.title?.trim()) { setError("Pick a project or give the block a title"); return; }
    if (!(Number(draft.durationMin) > 0)) { setError("Set a length"); return; }
    const early = parseTime(draft.earliest);
    const late = parseTime(draft.latest);
    if (early != null && late != null && late - early < Number(draft.durationMin)) {
      setError("The window is shorter than the block"); return;
    }
    onSave({ ...draft, title: draft.title?.trim() ?? "", perWeek: perWeekOf(draft) });
  }

  return (
    <Modal title={block.id ? "Edit standing block" : "New standing block"} onClose={onCancel} width={560}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", columnGap: 12 }}>
        <label style={label}>Project
          <select value={draft.project ?? ""} onChange={(e) => set({ project: e.target.value || undefined })} style={input}>
            <option value="">No project</option>
            {live.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label style={label}>Title on the calendar
          <input value={draft.title ?? ""} onChange={(e) => set({ title: e.target.value })} style={input}
            placeholder={blockTitle({ ...draft, title: "" }, settings, names)} />
        </label>
      </div>

      <div style={label}>Purpose
        <div style={{ marginTop: 4 }}>
          <Seg value={purposeOf(draft)} onChange={(p) => set({ purpose: p })}
            options={[["project", "Project work"], ["routine", "Routine"]]} />
        </div>
        <span style={{ display: "block", marginTop: 5, color: "var(--muted2)" }}>
          {purposeOf(draft) === "project"
            ? "Time for this project's tasks. The week planner counts it against their estimates."
            : "Protected time for recurring work that isn't on a task list. Uses up the week; covers no tasks."}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", columnGap: 12 }}>
        <label style={label}>Length
          <select value={String(draft.durationMin)} onChange={(e) => set({ durationMin: Number(e.target.value) })} style={input}>
            {[...new Set([...LENGTHS, Number(draft.durationMin)])].sort((a, b) => a - b).map((m) => (
              <option key={m} value={m}>{m < 60 ? `${m} min` : `${m / 60} h`}</option>
            ))}
          </select>
        </label>
        <label style={label}>How many a week
          <input type="number" min={1} max={14} value={draft.perWeek ?? 1}
            onChange={(e) => set({ perWeek: Number(e.target.value) || 1 })} style={input} />
        </label>
        <label style={label}>Preferred day
          <select value={draft.preferredDay ?? ""} onChange={(e) => set({ preferredDay: e.target.value as DayKey | "" })} style={input}>
            <option value="">Any day</option>
            {DAY_KEYS.map((d) => <option key={d} value={d}>{DAY_LABEL[d]}</option>)}
          </select>
        </label>
        <label style={label}>Preferred time
          <input type="time" value={draft.preferredTime ?? ""} onChange={(e) => set({ preferredTime: e.target.value })} style={input} />
        </label>
        <label style={label}>Not before
          <input type="time" value={draft.earliest ?? ""} onChange={(e) => set({ earliest: e.target.value })} style={input}
            placeholder={settings.workStart} />
        </label>
        <label style={label}>Done by
          <input type="time" value={draft.latest ?? ""} onChange={(e) => set({ latest: e.target.value })} style={input}
            placeholder={settings.workEnd} />
        </label>
      </div>
      <div style={{ fontSize: 11.5, color: "var(--muted2)", margin: "-6px 0 12px" }}>
        Leave the times blank to use your work day, {formatTime(parseTime(settings.workStart) ?? 480)}–{formatTime(parseTime(settings.workEnd) ?? 960)}.
        Copies of a block go on different days where they can, starting with the preferred day and then the days nearest it.
      </div>

      <div style={label}>Days it may go on
        <DayPicker value={days} onChange={(d) => set({ days: d })} />
        <span style={{ display: "block", marginTop: 5, color: "var(--muted2)" }}>
          {days.length ? "" : `None picked: your default days (${daysText(settings.defaultDays)}).`}
        </span>
      </div>

      {error && <div style={{ fontSize: 12, color: "var(--bad)", marginBottom: 10 }}>{error}</div>}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
        {onDelete && <button onClick={() => setConfirmDelete(true)} style={{ ...ghost, color: "var(--bad)", marginRight: "auto" }}>Delete</button>}
        <button onClick={onCancel} style={ghost}>Cancel</button>
        <button onClick={submit} disabled={saving} style={primary}>{saving ? "Saving…" : "Save"}</button>
      </div>

      {confirmDelete && onDelete && (
        <ConfirmDialog
          title="Delete this block?"
          message="It stops being placed from the next run. Blocks already on your calendar stay until that run clears them."
          onConfirm={() => { setConfirmDelete(false); onDelete(); }}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </Modal>
  );
}

function SettingsDialog({ settings, saving, onSave, onCancel }: {
  settings: BlockSettings; saving: boolean; onSave: (s: BlockSettings) => void; onCancel: () => void;
}) {
  const [draft, setDraft] = useState<BlockSettings>(settings);
  const set = (patch: Partial<BlockSettings>) => setDraft((d) => ({ ...d, ...patch }));
  const bad = (parseTime(draft.workEnd) ?? 0) <= (parseTime(draft.workStart) ?? 0);

  return (
    <Modal title="Hours & defaults" onClose={onCancel} width={500}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 12 }}>
        <label style={label}>Work day starts
          <input type="time" value={draft.workStart} onChange={(e) => set({ workStart: e.target.value })} style={input} />
        </label>
        <label style={label}>Work day ends
          <input type="time" value={draft.workEnd} onChange={(e) => set({ workEnd: e.target.value })} style={input} />
        </label>
      </div>
      <div style={label}>Default days
        <DayPicker value={draft.defaultDays} onChange={(d) => set({ defaultDays: d.length ? d : draft.defaultDays })} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 12 }}>
        <label style={label}>Title prefix
          <input value={draft.titlePrefix} onChange={(e) => set({ titlePrefix: e.target.value })} style={input} />
        </label>
        <label style={label}>Start times every
          <select value={draft.slotMin} onChange={(e) => set({ slotMin: Number(e.target.value) })} style={input}>
            {[5, 10, 15, 30, 60].map((m) => <option key={m} value={m}>{m} min</option>)}
          </select>
        </label>
        <label style={label}>Extra blocks are
          <select value={draft.blockLenMin} onChange={(e) => set({ blockLenMin: Number(e.target.value) })} style={input}>
            {[30, 45, 60, 90, 120].map((m) => <option key={m} value={m}>{m < 60 ? `${m} min` : `${m / 60} h`}</option>)}
          </select>
        </label>
      </div>
      <div style={{ fontSize: 11.5, color: "var(--muted2)", margin: "-4px 0 14px" }}>
        "Extra blocks" are the ones the week planner proposes on top of your standing blocks.
        Which calendars count as busy, and the blocks' colour, are set in the Apps Script.
      </div>

      <div style={{ ...label, borderTop: "1px solid var(--line)", paddingTop: 14 }}>
        <span style={{ display: "block", fontSize: 13, color: "var(--ink)", fontWeight: 500, marginBottom: 6 }}>Plan the week automatically</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 13, color: "var(--ink2)" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input type="checkbox" checked={draft.autoRun} onChange={(e) => set({ autoRun: e.target.checked })} />
            The first time I open Project Hub after
          </label>
          <select value={draft.autoRunDay} onChange={(e) => set({ autoRunDay: e.target.value as DayKey })} disabled={!draft.autoRun}
            aria-label="Planning day" style={{ ...input, width: "auto", marginTop: 0 }}>
            {DAY_KEYS.map((d) => <option key={d} value={d}>{DAY_LABEL[d]}</option>)}
          </select>
          at
          <input type="time" value={draft.autoRunTime} onChange={(e) => set({ autoRunTime: e.target.value })} disabled={!draft.autoRun}
            aria-label="Planning time" style={{ ...input, width: "auto", marginTop: 0 }} />
        </div>
        <span style={{ display: "block", marginTop: 6, color: "var(--muted2)" }}>
          {draft.autoRun
            ? `${planningNote(draft.autoRunDay)} On any device, phone included. Until you open the app, nothing is placed.`
            : "Off: blocks are placed only when you click Send to Google."}
        </span>
      </div>

      {bad && <div style={{ fontSize: 12, color: "var(--bad)", marginBottom: 10 }}>The work day ends before it starts.</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onCancel} style={ghost}>Cancel</button>
        <button onClick={() => {
          // Switched on just now: count planning times from now, so the week
          // you are in is not replanned the moment you save.
          const now = new Date();
          const since = draft.autoRun && !settings.autoRun
            ? localStamp(toIsoLocal(now), now.getHours() * 60 + now.getMinutes())
            : draft.autoRunSince;
          onSave({ ...draft, autoRunSince: since });
        }} disabled={saving || bad || parseTime(draft.autoRunTime) == null} style={primary}>{saving ? "Saving…" : "Save"}</button>
      </div>
    </Modal>
  );
}

/** Which week a planning day plans: Monday plans its own week; any other day, the next. */
function planningNote(day: DayKey): string {
  return day === "mon"
    ? "Plans the week that has just started (Monday to Sunday)."
    : "Plans the coming Monday to Sunday.";
}

/* -- Small parts ------------------------------------------------------------ */

function DayPicker({ value, onChange }: { value: DayKey[]; onChange: (d: DayKey[]) => void }) {
  return (
    <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
      {DAY_KEYS.map((d) => {
        const active = value.includes(d);
        return (
          <button key={d} type="button" aria-pressed={active}
            onClick={() => onChange(active ? value.filter((x) => x !== d) : DAY_KEYS.filter((x) => x === d || value.includes(x)))}
            style={{
              padding: "5px 10px", borderRadius: 999, fontSize: 12.5, cursor: "pointer",
              border: active ? "1px solid var(--accentLine)" : "1px solid var(--line2)",
              background: active ? "var(--accentTint)" : "var(--surface)", color: active ? "var(--accent)" : "var(--ink3)",
            }}>{DAY_LABEL[d]}</button>
        );
      })}
    </div>
  );
}

function Switch({ on, onChange, label: text }: { on: boolean; onChange: () => void; label: string }) {
  return (
    <button role="switch" aria-checked={on} aria-label={text} title={text} onClick={onChange} style={{
      width: 34, height: 20, borderRadius: 999, border: "none", padding: 2, cursor: "pointer",
      background: on ? "var(--accent)" : "var(--line2)", display: "flex", justifyContent: on ? "flex-end" : "flex-start",
    }}>
      <span style={{ width: 16, height: 16, borderRadius: "50%", background: "var(--surface)" }} />
    </button>
  );
}

